import { useEffect, useRef, useState } from "react";

export const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Animates a number towards `target` (ease-out). Used for counts and the finance monthly amount.
export function useAnimatedNumber(target, duration = 600) {
  const [val, setVal] = useState(reducedMotion() ? target : 0);
  const from = useRef(0);
  useEffect(() => {
    const to = Number(target) || 0;
    if (reducedMotion()) { setVal(to); from.current = to; return undefined; }
    const start = performance.now();
    const a = from.current;
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const v = a + (to - a) * (1 - Math.pow(1 - p, 3));
      setVal(v);
      if (p < 1) raf = requestAnimationFrame(step);
      else from.current = to;
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); };
  }, [target, duration]);
  return val;
}

// Adds .in-view to every .reveal element as it enters the screen, including ones added later (public website).
export function useRevealAll() {
  useEffect(() => {
    if (!("IntersectionObserver" in window) || reducedMotion()) {
      const all = () => document.querySelectorAll(".reveal:not(.in-view)").forEach((e) => e.classList.add("in-view"));
      all();
      const mo = new MutationObserver(all);
      mo.observe(document.body, { childList: true, subtree: true });
      return () => { mo.disconnect(); };
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("in-view"); io.unobserve(en.target); } });
    }, { rootMargin: "0px 0px -40px 0px" });
    const scan = () => document.querySelectorAll(".reveal:not(.in-view):not([data-rv])").forEach((e) => { e.dataset.rv = "1"; io.observe(e); });
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); io.disconnect(); };
  }, []);
}
